import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Badge, Row, Col, Card, Nav, InputGroup } from "react-bootstrap";
import {
  Home, Building2, Users, Plus, Trash2,
  CreditCard, ChevronRight, Zap, Mail, Copy, Check, Palette,
  AlertTriangle, Edit3, X, UserPlus, Shield, Globe, Crown
} from "lucide-react";
import { useWorkspaces } from "../../hooks/useWorkspaces";
import useStore from "../../utils/store";
import { userService } from "../../services/userService";
import WorkspaceSettings from "./WorkspaceSettings";

const FEATURE_ICONS = {
  1: '📋', 2: '📅', 3: '📝', 4: '🛒', 5: '🍽️', 6: '✅'
};

const ProfileAndWorkspaceManager = ({ auth }) => {
  const token = auth.user?.id_token;
  const {
    workspaces, selectedWorkspace, loading, setSelectedWorkspace,
    createWorkspace, updateWorkspace, deleteWorkspace, toggleFeature,
    inviteMember, addMember, updateMember, deleteMember, getInvites, getMyInvites, joinWorkspace, revokeInvite
  } = useWorkspaces({ token });

  // UI State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newWorkspace, setNewWorkspace] = useState({ name: "", type: "home" });

  // Edit Member State
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ displayName: '', color: '' });

  // Invite/Member State
  const [inviteForm, setInviteForm] = useState({ email: "", role: "member" });
  const [invites, setInvites] = useState([]);
  const [myInvites, setMyInvites] = useState([]);
  const [inviteStatus, setInviteStatus] = useState(null);

  const [artificialMember, setArtificialMember] = useState({
    displayName: "",
    color: "#0D6EFD",
    avatarUrl: ""
  });
  const [generatedInvite, setGeneratedInvite] = useState(null);
  const [copied, setCopied] = useState(false);

  // Workspace Rename State
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState("");

  const stats = { points: 240, aiCredits: 12 };

  /* ── Handlers ───────────────────────────────────────────── */

  const handleCreateArtificialMember = async (e) => {
    e.preventDefault();
    if (!artificialMember.displayName) return;
    try {
      await addMember(selectedWorkspace.id, {
        ...artificialMember,
        isArtificial: true
      });
      setArtificialMember({ displayName: "", color: "#0D6EFD", avatarUrl: "" });
    } catch (err) {
      alert("Failed to create artificial member");
    }
  };

  const handleUpdateMember = async (profileId, data) => {
    try {
      const payload = {
        displayName: data.displayName,
        color: data.color,
        avatarUrl: data.avatarUrl || ""
      };
      await updateMember(selectedWorkspace.id, profileId, payload);
      setEditingId(null);
    } catch (err) {
      console.error("Update failed:", err);
      alert("Failed to update member. Please check your inputs.");
    }
  };

  const handleCreate = async () => {
    if (!newWorkspace.name.trim()) return;
    try {
      const created = await createWorkspace(newWorkspace.name, newWorkspace.type);
      setNewWorkspace({ name: "", type: "home" });
      setShowCreateModal(false);
      handleSelectedWorkspace(created);
    } catch (err) {
      alert("Failed to create workspace");
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteWorkspace(selectedWorkspace.id);
      localStorage.removeItem("workspaceId");
      setShowDeleteModal(false);
    } catch (err) {
      alert("Failed to delete workspace");
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteStatus('sending');
    try {
      const res = await inviteMember(selectedWorkspace.id, inviteForm.email, inviteForm.role);
      setGeneratedInvite(res.inviteToken);
      setInviteStatus('success');
      loadInvites();
      setInviteForm({ email: "", role: "member" });
      setTimeout(() => setInviteStatus(null), 3000);
    } catch (err) {
      alert(err.response?.data?.error?.message || "Failed to create invite");
      setInviteStatus('error');
    }
  };

  const handleRevoke = async (inviteId) => {
    try {
      await revokeInvite(selectedWorkspace.id, inviteId);
      loadInvites();
    } catch (e) {
      alert("Failed to revoke invite");
    }
  };

  const loadInvites = async () => {
    if (selectedWorkspace?.id) {
      const data = await getInvites(selectedWorkspace.id);
      setInvites(data);
    }
  };

  const handleUpdateName = async () => {
    if (!newName.trim()) return;
    try {
      await updateWorkspace(selectedWorkspace.id, { name: newName });
      setSelectedWorkspace(prev => ({ ...prev, name: newName }));
      setIsEditingName(false);
    } catch (e) {
      console.error(e);
      alert("Failed to update workspace name");
    }
  };

  const handleAcceptInvite = async (inv) => {
    try {
      await joinWorkspace(inv.token, auth.user.profile.name || auth.user.profile.preferred_username);
      window.location.reload();
    } catch (e) {
      alert("Failed to join");
    }
  };

  const handleDeleteMember = async (profileId) => {
    if (!window.confirm("Are you sure you want to remove this member?")) return;
    try {
      await deleteMember(selectedWorkspace.id, profileId);
    } catch (err) {
      alert("Failed to remove member");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSelectedWorkspace = (ws) => {
    if (!ws) return;
    setSelectedWorkspace(ws);
    localStorage.setItem("workspaceId", ws.id);
    useStore.setState({
      workspaceId: ws.id,
      workspaceType: ws.type || 'home',
      workspaceName: ws.name || ''
    });
    userService.updateActiveWorkspace(ws.id).catch(err => {
      console.error("Failed to persist active workspace", err);
    });
  };

  /* ── Effects ────────────────────────────────────────────── */

  // Load invites when selected workspace changes
  useEffect(() => {
    loadInvites();
  }, [selectedWorkspace?.id]);

  // Sync selectedWorkspace to global store
  useEffect(() => {
    if (selectedWorkspace) {
      useStore.setState({
        workspaceId: selectedWorkspace.id,
        workspaceType: selectedWorkspace.type || 'home',
        workspaceName: selectedWorkspace.name || ''
      });
      localStorage.setItem("workspaceId", selectedWorkspace.id);
    }
  }, [selectedWorkspace]);

  // Load my invites
  useEffect(() => {
    const loadMyInvites = async () => {
      const data = await getMyInvites();
      setMyInvites(data);
    };
    loadMyInvites();
  }, [getMyInvites]);

  // Sync newName when workspace changes
  useEffect(() => {
    if (selectedWorkspace) {
      setNewName(selectedWorkspace.name);
      setIsEditingName(false);
    }
  }, [selectedWorkspace?.id]);

  /* ── Render ─────────────────────────────────────────────── */

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center vh-100">
      <div className="spinner-border text-primary" role="status"></div>
    </div>
  );

  return (
    <div className="ws-page">
      <Row className="g-0">

        {/* ─── LEFT SIDEBAR ─── */}
        <Col md={3} lg={2} className="ws-sidebar">
          <div className="d-flex align-items-center mb-4 px-2">
            <Zap className="text-primary me-2" size={22} />
            <span className="fw-bold fs-5">Arbeitsbereiche</span>
          </div>

          <p className="text-muted small fw-bold text-uppercase px-2 mb-2" style={{ letterSpacing: '0.5px', fontSize: '0.7rem' }}>
            Meine Arbeitsbereiche
          </p>

          <div className="flex-grow-1 overflow-auto mb-3 px-1">
            <Nav className="flex-column">
              {/* Pending Invites */}
              {myInvites.length > 0 && (
                <div className="mb-3">
                  <div className="px-2 text-primary small fw-bold mb-1">Offene Einladungen</div>
                  {myInvites.map(inv => (
                    <div key={inv.id} className="ws-invite-card">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fw-bold small">{inv.workspace_name}</span>
                        <Badge bg="primary" className="ws-badge-sm">{inv.role}</Badge>
                      </div>
                      <div className="tiny mb-2" style={{ opacity: 0.7 }}>Invited by: {inv.invited_by_name || 'Unknown'}</div>
                      <Button size="sm" variant="primary" className="w-100 py-0" style={{ fontSize: '0.8rem' }} onClick={() => handleAcceptInvite(inv)}>Annehmen</Button>
                    </div>
                  ))}
                </div>
              )}

              {workspaces.map((ws) => (
                <Nav.Link
                  key={ws.id}
                  onClick={() => handleSelectedWorkspace(ws)}
                  className={`ws-nav-item ${selectedWorkspace?.id === ws.id ? "active" : ""}`}
                >
                  <div className="text-truncate d-flex align-items-center">
                    {ws.type === "home" ? <Home size={15} className="me-2 flex-shrink-0" /> : <Building2 size={15} className="me-2 flex-shrink-0" />}
                    <span className="text-truncate">{ws.name}</span>
                  </div>
                  {selectedWorkspace?.id === ws.id && <ChevronRight size={14} className="flex-shrink-0" />}
                </Nav.Link>
              ))}
            </Nav>
          </div>

          <Button variant="outline-primary" size="sm" className="ws-btn-create" onClick={() => setShowCreateModal(true)}>
            <Plus size={16} className="me-1" /> Neuer Arbeitsbereich
          </Button>
        </Col>

        {/* ─── MAIN CONTENT ─── */}
        <Col md={9} lg={10} className="ws-main">
          {selectedWorkspace ? (
            <div className="ws-content">

              {/* ── Section: Header ── */}
              <div className="ws-header">
                <div className="d-flex align-items-start gap-3 flex-wrap">
                  <div className="ws-header-icon">
                    {selectedWorkspace.type === "home" ? <Home size={24} /> : <Building2 size={24} />}
                  </div>
                  <div className="flex-grow-1 min-width-0">
                    <Badge className="ws-type-badge mb-2">
                      {selectedWorkspace.type === "home" ? "Privat" : "Geschäftlich"}
                    </Badge>
                    {isEditingName ? (
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Control
                          className="ws-name-input"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleUpdateName()}
                          autoFocus
                        />
                        <Button variant="success" size="sm" className="rounded-pill px-3" onClick={handleUpdateName}>
                          <Check size={16} />
                        </Button>
                        <Button variant="light" size="sm" className="rounded-pill px-3" onClick={() => { setIsEditingName(false); setNewName(selectedWorkspace.name); }}>
                          <X size={16} />
                        </Button>
                      </div>
                    ) : (
                      <h2 className="ws-title" onClick={() => setIsEditingName(true)}>
                        {selectedWorkspace.name}
                        <Edit3 size={16} className="ws-title-edit-icon" />
                      </h2>
                    )}
                  </div>
                  <Button variant="outline-danger" size="sm" className="ws-delete-btn" onClick={() => setShowDeleteModal(true)} title="Delete workspace">
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>

              {/* ── Section: Stats ── */}
              <Row className="g-3 mb-4">
                <Col sm={6} md={4}>
                  <Card className="ws-stat-card">
                    <small className="ws-stat-label text-muted">Aktive Mitglieder</small>
                    <div className="d-flex align-items-center justify-content-between">
                      <h3 className="fw-bold mb-0 text-dark">{selectedWorkspace?.members?.length || 1}</h3>
                      <Users size={22} className="text-primary opacity-75" />
                    </div>
                  </Card>
                </Col>
              </Row>

              {/* ── Section: Members & Invites ── */}
              <div className="ws-section">
                <h5 className="ws-section-title">
                  <Users size={18} className="me-2" /> Teammitglieder
                </h5>

                <Row className="g-4">
                  {/* Members Table */}
                  <Col lg={8}>
                    <Card className="ws-card">
                      <Card.Body className="p-0">
                        <div className="table-responsive">
                          <table className="table align-middle mb-0 ws-members-table">
                            <thead>
                              <tr>
                                <th className="ps-4">Mitglied</th>
                                <th>Typ</th>
                                <th>Farbe</th>
                                <th className="text-end pe-4">Aktionen</th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* Inline Add Row */}
                              <tr className="ws-add-row">
                                <td className="ps-4">
                                  <div className="d-flex align-items-center">
                                    <div className="ws-avatar-placeholder me-3">
                                      <UserPlus size={16} />
                                    </div>
                                    <Form.Control
                                      size="sm"
                                      className="ws-input-subtle"
                                      placeholder="Neuer Profilname..."
                                      value={artificialMember.displayName}
                                      onChange={(e) => setArtificialMember({ ...artificialMember, displayName: e.target.value })}
                                      onKeyDown={(e) => e.key === 'Enter' && handleCreateArtificialMember(e)}
                                    />
                                  </div>
                                </td>
                                <td><Badge className="ws-badge-type ws-badge-artificial">Künstlich</Badge></td>
                                <td>
                                  <Form.Control
                                    type="color"
                                    className="ws-color-picker"
                                    value={artificialMember.color}
                                    onChange={(e) => setArtificialMember({ ...artificialMember, color: e.target.value })}
                                  />
                                </td>
                                <td className="text-end pe-4">
                                  <Button variant="success" size="sm" className="rounded-pill px-3" onClick={handleCreateArtificialMember}>
                                    <Plus size={14} className="me-1" /> Hinzufügen
                                  </Button>
                                </td>
                              </tr>

                              {/* Member Rows */}
                              {selectedWorkspace?.members?.map((m) => {
                                const isEditing = editingId === (m.id || m.userId);
                                return (
                                  <tr key={m.id || m.userId} className="ws-member-row">
                                    <td className="ps-4">
                                      <div className="d-flex align-items-center">
                                        <img
                                          src={m.avatarUrl || `https://ui-avatars.com/api/?name=${m.displayName}&background=f0f2f5&color=495057&bold=true`}
                                          className="ws-avatar me-3"
                                          width="36" height="36"
                                          alt=""
                                        />
                                        {isEditing ? (
                                          <Form.Control
                                            size="sm"
                                            className="ws-input-subtle"
                                            value={editForm.displayName}
                                            onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                                          />
                                        ) : (
                                          <div>
                                            <div className="fw-semibold">
                                              {m.displayName}
                                              {m.userId === auth.user?.profile?.sub && <span className="text-muted ms-1" style={{ fontSize: '0.8em' }}>(Du)</span>}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <Badge className={`ws-badge-type ${m.isArtificial ? 'ws-badge-artificial' : 'ws-badge-real'} me-1`}>
                                        {m.isArtificial ? 'Künstlich' : 'Echter Benutzer'}
                                      </Badge>
                                      <Badge className="ws-badge-role">
                                        <Shield size={10} className="me-1" />
                                        {m.role === 1 ? 'Admin' : 'Member'}
                                      </Badge>
                                    </td>
                                    <td>
                                      {isEditing ? (
                                        <Form.Control
                                          type="color"
                                          className="ws-color-picker"
                                          value={editForm.color}
                                          onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
                                        />
                                      ) : (
                                        <div className="ws-color-dot" style={{ backgroundColor: m.color || '#dee2e6' }} />
                                      )}
                                    </td>
                                    <td className="text-end pe-4">
                                      {isEditing ? (
                                        <div className="d-flex gap-1 justify-content-end">
                                          <Button variant="link" className="text-success p-1" onClick={() => {
                                            handleUpdateMember(m.id, editForm);
                                            setEditingId(null);
                                          }}>
                                            <Check size={18} />
                                          </Button>
                                          <Button variant="link" className="text-muted p-1" onClick={() => setEditingId(null)}>
                                            <X size={18} />
                                          </Button>
                                        </div>
                                      ) : (
                                        <div className="ws-action-btns">
                                          <Button variant="link" className="text-muted p-1" onClick={() => {
                                            setEditingId(m.id || m.userId);
                                            setEditForm({ displayName: m.displayName, color: m.color });
                                          }}>
                                            <Edit3 size={15} />
                                          </Button>
                                          {m.userId !== auth.user?.profile?.sub && (
                                            <Button variant="link" className="text-danger p-1" onClick={() => handleDeleteMember(m.id)}>
                                              <Trash2 size={15} />
                                            </Button>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* Invite Column */}
                  <Col lg={4}>
                    <Card className="ws-card ws-invite-section">
                      <Card.Body className="p-4">
                        <h6 className="fw-bold mb-3 d-flex align-items-center">
                          <Mail size={16} className="me-2 text-primary" /> Benutzer einladen
                        </h6>
                        <Form onSubmit={handleInvite}>
                          <Form.Group className="mb-3">
                            <Form.Control
                              type="email"
                              className="ws-input-subtle"
                              placeholder="email@company.com"
                              value={inviteForm.email}
                              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                            />
                          </Form.Group>
                          <Button type="submit" variant="primary" className="w-100 ws-btn-invite" disabled={inviteStatus === 'sending'}>
                            {inviteStatus === 'sending' ? 'Wird gesendet...' : 'Einladung senden'}
                          </Button>
                          {inviteStatus === 'success' && (
                            <div className="text-success small mt-2 d-flex align-items-center">
                              <Check size={14} className="me-1" /> Einladung gesendet!
                            </div>
                          )}
                        </Form>

                        {generatedInvite && (
                          <div className="ws-token-box mt-3">
                            <InputGroup size="sm">
                              <Form.Control readOnly value={generatedInvite} className="bg-transparent border-0 small" />
                              <Button variant="primary" size="sm" onClick={() => copyToClipboard(generatedInvite)}>
                                {copied ? <Check size={14} /> : <Copy size={14} />}
                              </Button>
                            </InputGroup>
                            <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>Fallback-Token falls E-Mail fehlschlägt</div>
                          </div>
                        )}

                        {/* Pending Invites */}
                        {invites.length > 0 && (
                          <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--ws-border)' }}>
                            <h6 className="fw-bold small text-muted mb-3">Offene Einladungen</h6>
                            <div className="d-flex flex-column gap-2">
                              {invites.map(inv => (
                                <div key={inv.id} className="ws-pending-invite">
                                  <div className="text-truncate" style={{ maxWidth: '150px' }}>
                                    <div className="fw-bold small" title={inv.email}>{inv.email}</div>
                                    <div style={{ fontSize: '0.7rem', color: 'var(--ws-text-muted)' }}>
                                      {new Date(inv.created_at).toLocaleDateString()}
                                    </div>
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <Badge className="ws-badge-role">{inv.role}</Badge>
                                    <Button variant="link" className="text-danger p-0" title="Revoke" onClick={() => handleRevoke(inv.id)}>
                                      <X size={14} />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>

              {/* ── Section: Workspace Settings ── */}
              <div className="ws-section">
                <WorkspaceSettings token={token} workspaceId={selectedWorkspace?.id} />
              </div>

              {/* ── Section: Funktionen (hidden for now) ── */}

            </div>
          ) : (
            <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted py-5">
              <Globe size={48} className="mb-3 text-primary opacity-50" />
              <h4 className="fw-semibold">Arbeitsbereich auswählen</h4>
              <p className="text-muted">Wähle einen Arbeitsbereich aus der Seitenleiste</p>
            </div>
          )}
        </Col>
      </Row>

      {/* ─── DELETE CONFIRMATION MODAL ─── */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Body className="text-center p-4">
          <div className="text-danger mb-3"><AlertTriangle size={48} /></div>
          <h4 className="fw-bold">Arbeitsbereich löschen?</h4>
          <p className="text-muted">
            Diese Aktion ist unwiderruflich. Alle Notizen, Checklisten und zugehörigen Daten in
            <strong> {selectedWorkspace?.name}</strong> werden dauerhaft gelöscht.
          </p>
          <div className="d-flex gap-2 mt-4">
            <Button variant="light" className="flex-grow-1" onClick={() => setShowDeleteModal(false)}>Abbrechen</Button>
            <Button variant="danger" className="flex-grow-1" onClick={confirmDelete}>Alles löschen</Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* ─── CREATE MODAL ─── */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold">Arbeitsbereich erstellen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold">NAME</Form.Label>
            <Form.Control
              value={newWorkspace.name}
              onChange={e => setNewWorkspace({ ...newWorkspace, name: e.target.value })}
              placeholder="z.B. Meine Projekte"
            />
          </Form.Group>
          {/* Business workspace creation disabled for now */}
          <div className="d-flex gap-2">
            <Button variant="primary" className="flex-grow-1" disabled>Privat</Button>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="primary" className="w-100" onClick={handleCreate}>Arbeitsbereich erstellen</Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        /* ── Design Tokens ── */
        :root {
          --ws-bg: #f8f9fb;
          --ws-surface: #ffffff;
          --ws-border: #e8ecf1;
          --ws-text: #1a1d21;
          --ws-text-muted: #6b7280;
          --ws-primary: #4f6ef7;
          --ws-primary-soft: #eef2ff;
          --ws-success: #22c55e;
          --ws-danger: #ef4444;
          --ws-radius: 12px;
          --ws-radius-lg: 16px;
          --ws-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06);
          --ws-shadow-md: 0 4px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04);
        }

        /* ── Page Layout ── */
        .ws-page {
          min-height: 100vh;
          background: var(--ws-bg);
        }

        /* ── Sidebar ── */
        .ws-sidebar {
          background: var(--ws-surface);
          border-right: 1px solid var(--ws-border);
          height: 100vh;
          padding: 1.25rem;
          display: flex;
          flex-direction: column;
          position: sticky;
          top: 0;
        }

        .ws-nav-item {
          border-radius: 10px !important;
          margin-bottom: 2px;
          padding: 0.5rem 0.75rem !important;
          display: flex !important;
          align-items: center;
          justify-content: space-between;
          color: var(--ws-text) !important;
          font-weight: 500;
          font-size: 0.9rem;
          transition: all 0.15s ease;
        }
        .ws-nav-item:hover {
          background: var(--ws-primary-soft);
          color: var(--ws-primary) !important;
        }
        .ws-nav-item.active {
          background: var(--ws-primary) !important;
          color: #fff !important;
          box-shadow: 0 2px 8px rgba(79,110,247,0.3);
        }

        .ws-invite-card {
          padding: 0.65rem;
          background: var(--ws-primary-soft);
          border-radius: 10px;
          margin-bottom: 0.5rem;
          border: 1px solid rgba(79,110,247,0.15);
        }

        .ws-btn-create {
          width: 100%;
          border: 2px dashed var(--ws-border) !important;
          border-radius: 10px !important;
          padding: 0.5rem !important;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .ws-btn-create:hover {
          border-color: var(--ws-primary) !important;
          background: var(--ws-primary-soft) !important;
        }

        /* ── Main Area ── */
        .ws-main {
          height: 100vh;
          overflow-y: auto;
          padding: 2rem;
        }
        @media (min-width: 992px) {
          .ws-main { padding: 2.5rem 3rem; }
        }

        .ws-content {
          max-width: 1100px;
          margin: 0 auto;
        }

        /* ── Header ── */
        .ws-header {
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid var(--ws-border);
        }

        .ws-header-icon {
          width: 48px;
          height: 48px;
          border-radius: var(--ws-radius);
          background: var(--ws-primary-soft);
          color: var(--ws-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .ws-type-badge {
          background: var(--ws-primary-soft) !important;
          color: var(--ws-primary) !important;
          font-weight: 600;
          font-size: 0.65rem !important;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 0.25rem 0.65rem !important;
          border: none !important;
        }

        .ws-title {
          font-weight: 700;
          font-size: 1.6rem;
          color: var(--ws-text);
          margin-bottom: 0;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          line-height: 1.3;
        }
        .ws-title-edit-icon {
          opacity: 0;
          transition: opacity 0.15s ease;
          color: var(--ws-text-muted);
        }
        .ws-title:hover .ws-title-edit-icon {
          opacity: 0.6;
        }

        .ws-name-input {
          font-size: 1.3rem;
          font-weight: 700;
          border: 2px solid var(--ws-primary);
          border-radius: 10px;
          padding: 0.35rem 0.75rem;
          max-width: 400px;
        }

        .ws-delete-btn {
          border-radius: 10px !important;
          padding: 0.45rem 0.65rem !important;
          border: 1px solid #fecaca !important;
          color: var(--ws-danger) !important;
          transition: all 0.2s ease;
        }
        .ws-delete-btn:hover {
          background: #fef2f2 !important;
        }

        /* ── Stats Cards ── */
        .ws-stat-card {
          border: none !important;
          border-radius: var(--ws-radius) !important;
          padding: 1.15rem;
          box-shadow: var(--ws-shadow);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .ws-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--ws-shadow-md);
        }
        .ws-stat-primary {
          background: linear-gradient(135deg, #4f6ef7 0%, #7c5cf7 100%) !important;
          color: #fff;
        }
        .ws-stat-label {
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        /* ── Sections ── */
        .ws-section {
          margin-bottom: 2rem;
        }

        .ws-section-title {
          font-weight: 700;
          font-size: 1rem;
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
          color: var(--ws-text);
        }
        .ws-section-title svg {
          color: var(--ws-primary);
        }

        /* ── Cards ── */
        .ws-card {
          border: 1px solid var(--ws-border) !important;
          border-radius: var(--ws-radius-lg) !important;
          box-shadow: var(--ws-shadow);
          overflow: hidden;
          background: var(--ws-surface);
        }

        /* ── Members Table ── */
        .ws-members-table thead th {
          border: none;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--ws-text-muted);
          padding: 0.85rem 0.75rem;
          background: #f9fafb;
        }
        .ws-members-table tbody td {
          padding: 0.75rem;
          border-color: var(--ws-border);
          vertical-align: middle;
        }
        .ws-add-row {
          background: #f0f7ff !important;
        }
        .ws-add-row td {
          border-bottom: 2px solid var(--ws-primary-soft) !important;
        }
        .ws-member-row {
          transition: background 0.15s ease;
        }
        .ws-member-row:hover {
          background: #fafbfc;
        }

        .ws-avatar {
          border-radius: 50%;
          border: 2px solid var(--ws-border);
          object-fit: cover;
        }
        .ws-avatar-placeholder {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: var(--ws-primary-soft);
          color: var(--ws-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .ws-input-subtle {
          border: 1.5px solid var(--ws-border) !important;
          border-radius: 8px !important;
          font-size: 0.875rem;
          padding: 0.4rem 0.7rem;
          transition: border-color 0.15s ease;
        }
        .ws-input-subtle:focus {
          border-color: var(--ws-primary) !important;
          box-shadow: 0 0 0 3px rgba(79,110,247,0.1) !important;
        }

        .ws-color-picker {
          width: 32px !important;
          height: 32px !important;
          padding: 2px !important;
          border: 2px solid var(--ws-border) !important;
          border-radius: 8px !important;
          cursor: pointer;
          background: transparent;
        }

        .ws-color-dot {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          border: 2px solid rgba(0,0,0,0.06);
        }

        /* ── Badges ── */
        .ws-badge-type {
          font-weight: 600;
          font-size: 0.68rem !important;
          padding: 0.2rem 0.5rem !important;
          border-radius: 6px !important;
          border: none !important;
        }
        .ws-badge-artificial {
          background: #f0fdf4 !important;
          color: #16a34a !important;
        }
        .ws-badge-real {
          background: var(--ws-primary-soft) !important;
          color: var(--ws-primary) !important;
        }
        .ws-badge-role {
          background: #f3f4f6 !important;
          color: var(--ws-text-muted) !important;
          font-weight: 500;
          font-size: 0.68rem !important;
          padding: 0.2rem 0.5rem !important;
          border-radius: 6px !important;
          border: none !important;
        }
        .ws-badge-sm {
          font-size: 0.65rem !important;
          padding: 0.15rem 0.4rem !important;
        }

        /* ── Action Buttons ── */
        .ws-action-btns {
          opacity: 0.4;
          transition: opacity 0.15s ease;
        }
        .ws-member-row:hover .ws-action-btns {
          opacity: 1;
        }

        /* ── Invite Section ── */
        .ws-invite-section {
          background: linear-gradient(180deg, #fafbff 0%, #ffffff 100%) !important;
        }

        .ws-btn-invite {
          border-radius: 10px !important;
          padding: 0.55rem !important;
          font-weight: 600;
          letter-spacing: 0.2px;
        }

        .ws-token-box {
          padding: 0.65rem;
          background: var(--ws-primary-soft);
          border-radius: 10px;
          border: 1px solid rgba(79,110,247,0.15);
        }

        .ws-pending-invite {
          padding: 0.5rem 0.65rem;
          background: #fff;
          border-radius: 8px;
          border: 1px solid var(--ws-border);
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: border-color 0.15s ease;
        }
        .ws-pending-invite:hover {
          border-color: #d1d5db;
        }

        /* ── Feature Cards ── */
        .ws-feature-card {
          border: 1px solid var(--ws-border) !important;
          border-radius: var(--ws-radius) !important;
          box-shadow: var(--ws-shadow);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          background: var(--ws-surface);
        }
        .ws-feature-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--ws-shadow-md);
        }
        .ws-feature-icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
          background: #f3f4f6;
          color: var(--ws-text-muted);
          flex-shrink: 0;
          transition: all 0.2s ease;
        }
        .ws-feature-icon.active {
          background: var(--ws-primary-soft);
          color: var(--ws-primary);
        }

        .ws-switch .form-check-input {
          width: 2.75em;
          height: 1.4em;
          cursor: pointer;
          border-radius: 1em;
        }
        .ws-switch .form-check-input:checked {
          background-color: var(--ws-primary);
          border-color: var(--ws-primary);
        }

        /* ── Pro Badge ── */
        .ws-badge-pro {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
          color: #fff !important;
          font-weight: 700;
          font-size: 0.6rem !important;
          padding: 0.15rem 0.45rem !important;
          border-radius: 6px !important;
          border: none !important;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .ws-feature-locked {
          opacity: 0.65;
        }
        .ws-feature-locked .ws-switch .form-check-input {
          cursor: not-allowed;
        }

        /* ── Responsive ── */
        @media (max-width: 767.98px) {
          .ws-sidebar {
            height: auto;
            position: relative;
            border-right: none;
            border-bottom: 1px solid var(--ws-border);
          }
          .ws-main {
            height: auto;
            padding: 1.25rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfileAndWorkspaceManager;